import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { Role } from "data/models";
import { Plus, Trash2 } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { object, string } from "yup";

interface MemberInvitationsProps {
  isOpen: boolean;
  isLoading: boolean;
  isError?: boolean;
  handleClose: () => void;
  handleSubmit: (itemList: InvitationItem[]) => void;
  items?: string[];
  orgName: string;
}

export interface InvitationItem {
  email: string;
  role: Role;
}

export const invitationValidator = object({
  email: string().email("Enter a valid email").required("Email is required"),
});

const addAnotherItem = (
  itemList: InvitationItem[],
  setItemList: Dispatch<SetStateAction<InvitationItem[]>>
) => {
  if (itemList.length < 5)
    setItemList([...itemList, { email: "", role: Role.ADMIN }]);
};

const removeItem = (
  // item: string,
  index: number,
  itemList: InvitationItem[],
  setItemList: Dispatch<SetStateAction<InvitationItem[]>>
) => {
  if (itemList.length <= 1) return;
  const nextList = [...itemList];
  nextList.splice(index, 1);
  setItemList(nextList);
};

const hasDuplicateEmails = (itemList: InvitationItem[]): boolean => {
  const emails = itemList.map((i) => i.email);
  return new Set(emails).size !== emails.length;
};
const isInvalidEmail = (email: string): boolean => {
  if (!email) return true;
  const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  return !emailRegex.test(email);
};

const isErrorList = (itemList: InvitationItem[]): boolean => {
  return (
    itemList.some((item) => isInvalidEmail(item.email)) ||
    hasDuplicateEmails(itemList)
  );
};

const handleEmailChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  index: number,
  itemList: InvitationItem[],
  setItemList: Dispatch<SetStateAction<InvitationItem[]>>
) => {
  const nextList = [...itemList];
  nextList[index].email = event.target.value;
  setItemList(nextList);
};

const handleRoleChange = (
  event: SelectChangeEvent<Role>,
  index: number,
  itemList: InvitationItem[],
  setItemList: Dispatch<SetStateAction<InvitationItem[]>>
) => {
  const nextList = [...itemList];
  nextList[index].role = event.target.value as Role;
  setItemList(nextList);
};

const resetAndClose = (
  setItemList: Dispatch<SetStateAction<InvitationItem[]>>,
  handleCloseProp: () => void
) => {
  setItemList([{ email: "", role: Role.ADMIN }]);
  handleCloseProp();
};
export const MemberInvitationModal = (props: MemberInvitationsProps) => {
  const [itemList, setItemList] = useState<InvitationItem[]>([
    { email: "", role: Role.ADMIN },
  ]);

  const { isPermitted } = usePermissions();

  return (
    <ConfirmationModal
      isLoading={props.isLoading}
      isError={props.isError}
      open={props.isOpen}
      header={`Invite to ${props.orgName}`}
      body={""}
      leftButtonText={"Cancel"}
      leftButtonColor="primary"
      rightButtonText={"Invite"}
      rightButtonColor="primary"
      handleClose={() => resetAndClose(setItemList, props.handleClose)}
      handleRightButton={() => {
        props.handleSubmit(itemList);
        setItemList([{ email: "", role: Role.ADMIN }]);
      }}
      handleLeftButton={() => resetAndClose(setItemList, props.handleClose)}
      rightButtonDisabled={isErrorList(itemList) || props.isLoading}
      leftButtonDisabled={props.isLoading}
    >
      <Box
        sx={{
          margin: "0px",
          gap: "0px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        {itemList.map((item, index) => (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "8px",
            }}
            key={index}
          >
            <TextField
              id={`email-${index}`}
              key={index}
              value={item.email}
              size="small"
              fullWidth
              type="email"
              variant="outlined"
              error={isInvalidEmail(item.email)}
              helperText={
                isInvalidEmail(item.email) ? "Please enter a valid email" : " "
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleEmailChange(event, index, itemList, setItemList)
              }
            />
            <Box sx={{}}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id={`role-label-${index}`}>Role</InputLabel>
                <Select
                  onChange={(e: SelectChangeEvent<Role>) =>
                    handleRoleChange(e, index, itemList, setItemList)
                  }
                  value={item.role}
                  label="Role"
                  disabled={!isPermitted([PermissionsKeys.rolesCreate])}
                >
                  {Object.values(Role)
                    .filter((role) => role !== Role.OWNER)
                    .map((role) => (
                      <MenuItem key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{}}>
              <IconButton
                sx={{ margin: "0px", paddingLeft: "0px", paddingRight: "0px" }}
                disabled={itemList.length <= 1}
                onClick={() => removeItem(index, itemList, setItemList)}
              >
                <Trash2 />
              </IconButton>
            </Box>
          </Box>
        ))}
        <IconButton
          disabled={
            itemList[itemList.length - 1].email === "" || itemList.length >= 5
          }
          color="primary"
          sx={{ margin: "0px", padding: "0px", paddingRight: "0px" }}
          onClick={() => addAnotherItem(itemList, setItemList)}
        >
          <Plus />
        </IconButton>
      </Box>
    </ConfirmationModal>
  );
};
